import { RouterProvider } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import TokenDebugger from './components/common/TokenDebugger';
import { setupTenantInterceptor } from './services/tenantInterceptor';
import router from './routes';

// Initialize multi-tenant API interceptor
setupTenantInterceptor();

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Jiigy-inspired theme configuration with green color scheme
const theme = {
  token: {
    colorPrimary: '#4ade80',        // Jiigy green primary
    colorSuccess: '#22c55e',        // Success green
    colorWarning: '#f59e0b',        // Warning amber
    colorError: '#ef4444',          // Error red
    colorInfo: '#06b6d4',           // Info cyan
    borderRadius: 12,               // More rounded corners like Jiigy
    borderRadiusLG: 16,
    borderRadiusSM: 8,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeXL: 20,
    lineHeight: 1.6,                // Better line height for readability
    wireframe: false,
    motionDurationMid: '0.3s',
    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgBase: '#f8fdf9',         // Very light green tint
    colorBgLayout: '#f0fdf4',       // Light green background like Jiigy
    colorBorder: '#d1fae5',         // Light green borders
    colorText: '#064e3b',           // Dark green text for better contrast
    colorTextSecondary: '#065f46',  // Medium green secondary text
    colorTextTertiary: '#047857',   // Tertiary green text
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      headerPadding: '0 32px',
      siderBg: '#ffffff',           // Clean white sidebar like Jiigy
      bodyBg: '#f0fdf4',            // Light green body background
      headerHeight: 80,
      headerColor: '#064e3b',
      headerBoxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)', // Green shadow
    },
    Menu: {
      itemColor: '#065f46',
      itemHoverColor: '#064e3b',
      itemSelectedColor: '#ffffff',
      itemSelectedBg: '#4ade80',     // Jiigy green for selected items
      itemHoverBg: '#ecfdf5',        // Very light green hover
      itemBorderRadius: 12,
      itemMarginBlock: 8,
      itemMarginInline: 16,
      itemActiveBg: '#4ade80',
      itemHeight: 48,               // Slightly taller menu items
      itemPaddingInline: 20,
    },
    Card: {
      borderRadius: 16,             // Rounded like Jiigy cards
      headerBg: 'transparent',
      colorBorderSecondary: '#d1fae5',
      boxShadow: '0 4px 6px rgba(34, 197, 94, 0.05)', // Subtle green shadow
      boxShadowSecondary: '0 8px 25px rgba(34, 197, 94, 0.1)',
      headerFontSize: 18,
      headerFontWeight: 600,
      colorBgContainer: '#ffffff',
    },
    Button: {
      borderRadius: 12,             // More rounded buttons
      controlHeight: 44,            // Taller buttons
      fontWeight: 600,              // Bolder text like Jiigy
      defaultShadow: '0 2px 4px rgba(34, 197, 94, 0.1)',
      primaryShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
      colorPrimary: '#4ade80',
      colorPrimaryHover: '#22c55e',
      colorPrimaryActive: '#16a34a',
    },
    Input: {
      borderRadius: 12,             // Rounded inputs
      controlHeight: 44,
      colorBorder: '#d1fae5',
      hoverBorderColor: '#a7f3d0',
      activeBorderColor: '#4ade80',
      activeShadow: '0 0 0 3px rgba(74, 222, 128, 0.2)',
      colorBgContainer: '#ffffff',
    },
    Table: {
      borderColor: '#d1fae5',
      headerBg: '#f0fdf4',          // Light green header
      headerColor: '#065f46',
      rowHoverBg: '#ecfdf5',
      headerSplitColor: 'transparent',
      rowSelectedBg: '#d1fae5',
      rowSelectedHoverBg: '#bbf7d0',
      colorBgContainer: '#ffffff',
    },
    Select: {
      colorBorder: '#d1fae5',
      optionSelectedBg: '#d1fae5',
      optionSelectedColor: '#065f46',
      optionSelectedFontWeight: 600,
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
    },
    Dropdown: {
      colorBgElevated: '#ffffff',
      colorBorder: '#d1fae5',
    },
    Notification: {
      colorBgElevated: '#ffffff',
    },
    Modal: {
      contentBg: '#ffffff',
      headerBg: '#f0fdf4',
    },
    Drawer: {
      colorBgElevated: '#ffffff',
    },
    Tag: {
      borderRadiusSM: 8,            // Rounded tags
    },
    Badge: {
      colorBgContainer: '#4ade80',
    },
    Progress: {
      defaultColor: '#4ade80',
    },
    Switch: {
      colorPrimary: '#4ade80',
      colorPrimaryHover: '#22c55e',
    },
    Slider: {
      colorPrimary: '#4ade80',
      colorPrimaryBorderHover: '#22c55e',
    },
    Radio: {
      colorPrimary: '#4ade80',
    },
    Checkbox: {
      colorPrimary: '#4ade80',
    },
    DatePicker: {
      colorPrimary: '#4ade80',
      activeBorderColor: '#4ade80',
    },
    Statistic: {
      titleFontSize: 14,
      contentFontSize: 24,
      contentFontWeight: 700,
    },
  },
};

function App() {
  return (
    <ConfigProvider
      theme={theme}
      componentSize="middle"
      componentDisabled={false}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
            {/* Token debugger - only visible in development */}
            <TokenDebugger />
          </AuthProvider>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;